import React, { useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Eye, X, FileText } from 'lucide-react';
import { useObrasFixas } from '../../context/ObrasFixasContextSupabase';
import L from 'leaflet';

// Configurar ícones do Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.8.0/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.8.0/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.8.0/images/marker-shadow.png',
});

// Função para criar ícones personalizados baseados no status
const criarIconePersonalizado = (status) => {
  const cor = getCorPorStatus(status);
  
  return L.divIcon({
    className: 'obra-fixa-marker',
    html: `<div style="background-color: ${cor}; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white; box-shadow: 0 0 4px rgba(0,0,0,0.5);"></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8]
  });
};

// Função auxiliar para determinar a cor baseada no status
function getCorPorStatus(status) {
  switch (status) {
    case 'Planejada':
      return '#ffc107'; // Amarelo
    case 'Em execução':
      return '#28a745'; // Verde
    case 'Finalizada':
      return '#007bff'; // Azul
    default:
      return '#6c757d'; // Cinza
  }
}

const MapaObrasFixas = () => {
  const { obrasFixas } = useObrasFixas();
  const [obraFixaSelecionada, setObraFixaSelecionada] = useState(null);
  
  // Coordenadas de Conceição do Castelo - ES
  const centroConceicaoCastelo = [-20.359292, -41.247282];

  // Função getCorPorStatus já definida globalmente

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const formatarData = (data) => {
    return new Date(data).toLocaleDateString('pt-BR');
  };
  
  // Formatar percentual
  const formatarPercentual = (valor) => {
    return `${valor.toFixed(2)}%`;
  };
  
  // Calcular valor total dos itens
  const calcularValorTotalItens = (itens) => {
    if (!itens || itens.length === 0) return 0;
    return itens.reduce((total, item) => total + (item.total || 0), 0);
  };
  
  // Calcular progresso geral dos itens
  const calcularProgressoGeral = (itens) => {
    if (!itens || itens.length === 0) return 0;
    
    const totalValor = calcularValorTotalItens(itens);
    if (totalValor === 0) return 0;
    
    const totalMedido = itens.reduce((total, item) => total + (item.totalMedido || 0), 0);
    return (totalMedido / totalValor) * 100;
  };
  
  // Função para determinar a cor da barra de progresso
  const getProgressColor = (progresso) => {
    if (progresso < 30) return '#dc3545'; // Vermelho
    if (progresso < 70) return '#ffc107'; // Amarelo
    return '#28a745'; // Verde
  };

  const abrirDetalhesObraFixa = (obraFixa) => {
    setObraFixaSelecionada(obraFixa);
  };

  return (
    <div style={{ 
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Header compacto */}
      <div style={{ 
        padding: '8px 15px', 
        backgroundColor: 'white', 
        borderBottom: '1px solid #dee2e6',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        flexShrink: 0,
        zIndex: 1000
      }}>
        <h2 style={{ margin: '0 0 4px 0', fontSize: '18px' }}>Mapa das Obras Fixas</h2>
        <p style={{ color: '#666', margin: 0, fontSize: '12px' }}>
          Clique nos marcadores para informações.
          <span style={{ marginLeft: '8px' }}>
            <span style={{ color: '#ffc107', fontWeight: 'bold' }}>■ Planejada</span>
            <span style={{ color: '#28a745', fontWeight: 'bold', marginLeft: '6px' }}>■ Em execução</span>
            <span style={{ color: '#007bff', fontWeight: 'bold', marginLeft: '6px' }}>■ Finalizada</span>
          </span>
        </p>
      </div>

      {/* Mapa ocupando todo o espaço restante */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <MapContainer
          center={centroConceicaoCastelo}
          zoom={13}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          
          {obrasFixas.map(obraFixa => (
              obraFixa.localizacao && obraFixa.localizacao.latitude && obraFixa.localizacao.longitude && (
                <Marker 
                  key={obraFixa.id} 
                  position={[obraFixa.localizacao.latitude, obraFixa.localizacao.longitude]}
                  icon={criarIconePersonalizado(obraFixa.status)}
                >
                  <Popup>
                    <div style={{ minWidth: '200px' }}>
                      <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>{obraFixa.nome}</h4>
                      {obraFixa.localizacao.nome && (
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>Local:</strong> {obraFixa.localizacao.nome}
                        </p>
                      )}
                      <p style={{ margin: '5px 0', fontSize: '14px' }}>
                        <strong>Empreiteiro:</strong> {obraFixa.empreiteiro}
                      </p>
                      <p style={{ margin: '5px 0', fontSize: '14px' }}>
                        <strong>Status:</strong> <span style={{ color: getCorPorStatus(obraFixa.status) }}>{obraFixa.status}</span>
                      </p>
                      <p style={{ margin: '5px 0', fontSize: '14px' }}>
                        <strong>Valor:</strong> {formatarMoeda(obraFixa.valor)}
                      </p>
                      <button 
                        className="button" 
                        style={{ marginTop: '10px', fontSize: '12px', padding: '6px 12px' }}
                        onClick={() => abrirDetalhesObraFixa(obraFixa)}
                      >
                        <Eye size={14} />
                        Ver Detalhes
                      </button>
                    </div>
                  </Popup>
                </Marker>
              )
            ))}
        </MapContainer>
      </div>

      {/* Modal de detalhes da obra fixa */}
      {obraFixaSelecionada && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3 className="modal-title">Detalhes da Obra Fixa</h3>
              <button className="close-button" onClick={() => setObraFixaSelecionada(null)}>
                <X size={20} />
              </button>
            </div>

            <div>
              <h4 style={{ color: '#333', marginBottom: '15px' }}>{obraFixaSelecionada.nome}</h4>
              
              <div style={{ display: 'grid', gap: '10px', marginBottom: '15px' }}>
                <div>
                  <strong>Empreiteiro:</strong> {obraFixaSelecionada.empreiteiro}
                </div>
                <div>
                  <strong>Valor:</strong> {formatarMoeda(obraFixaSelecionada.valor)}
                </div>
                <div>
                  <strong>Status:</strong> 
                  <span 
                    style={{ 
                      color: getCorPorStatus(obraFixaSelecionada.status),
                      fontWeight: 'bold',
                      marginLeft: '8px'
                    }}
                  >
                    {obraFixaSelecionada.status}
                  </span>
                </div>
                <div>
                  <strong>Data de Início:</strong> {formatarData(obraFixaSelecionada.dataInicio)}
                </div>
                <div>
                  <strong>Previsão de Término:</strong> {formatarData(obraFixaSelecionada.dataPrevisaoTermino)}
                </div>
                {obraFixaSelecionada.localizacao && obraFixaSelecionada.localizacao.nome && (
                  <div style={{ 
                    marginTop: '5px', 
                    padding: '8px', 
                    backgroundColor: '#f3e5f5', 
                    borderRadius: '6px', 
                    color: '#6a1b9a',
                    fontWeight: 'bold'
                  }}>
                    Local: {obraFixaSelecionada.localizacao.nome}
                  </div>
                )}
                {obraFixaSelecionada.localizacao && obraFixaSelecionada.localizacao.descricao && (
                  <div style={{ 
                    padding: '8px', 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: '6px', 
                    color: '#666',
                    fontSize: '14px'
                  }}>
                    <strong>Descrição do local:</strong> {obraFixaSelecionada.localizacao.descricao}
                  </div>
                )}
              </div>

              {/* Itens e Medições da Obra */}
              {obraFixaSelecionada.itens && obraFixaSelecionada.itens.length > 0 && (
                <div style={{ marginBottom: '15px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                    <FileText size={18} />
                    <strong>Itens e Medições ({obraFixaSelecionada.itens.length}):</strong>
                  </div>
                  
                  {/* Resumo dos Itens */}
                  <div style={{ 
                    padding: '12px', 
                    backgroundColor: '#e8f5e9', 
                    borderRadius: '6px',
                    marginBottom: '10px'
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                      <div>
                        <strong>Valor Total dos Itens:</strong> {formatarMoeda(calcularValorTotalItens(obraFixaSelecionada.itens))}
                      </div>
                      <div>
                        <strong>Valor Medido:</strong> {formatarMoeda(obraFixaSelecionada.itens.reduce((total, item) => total + (item.totalMedido || 0), 0))}
                      </div>
                    </div>
                    
                    {/* Barra de Progresso */}
                    <div style={{ marginTop: '10px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                        <strong>Progresso Geral:</strong>
                        <span>{formatarPercentual(calcularProgressoGeral(obraFixaSelecionada.itens))}</span>
                      </div>
                      <div style={{ 
                        width: '100%', 
                        height: '20px', 
                        backgroundColor: '#e9ecef',
                        borderRadius: '10px',
                        overflow: 'hidden'
                      }}>
                        <div style={{ 
                          width: `${calcularProgressoGeral(obraFixaSelecionada.itens)}%`, 
                          height: '100%', 
                          backgroundColor: getProgressColor(calcularProgressoGeral(obraFixaSelecionada.itens)),
                          borderRadius: '10px'
                        }}></div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Lista de Itens Resumida */}
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {obraFixaSelecionada.itens.map(item => (
                      <div key={item.id} style={{
                        padding: '8px 12px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        marginBottom: '6px',
                        fontSize: '14px'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <div>
                            <strong>{item.item} - {item.descricao}</strong>
                          </div>
                          <div>
                            {formatarMoeda(item.total)}
                          </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666', marginTop: '5px' }}>
                          <div>
                            {item.quantidade} {item.unidade} x {formatarMoeda(item.preco)}
                          </div>
                          <div>
                            Medido: {formatarPercentual(item.progresso || 0)}
                          </div>
                        </div>
                        
                        {/* Mini Barra de Progresso */}
                        <div style={{ 
                          width: '100%', 
                          height: '6px', 
                          backgroundColor: '#e9ecef',
                          borderRadius: '3px',
                          overflow: 'hidden',
                          marginTop: '5px'
                        }}>
                          <div style={{ 
                            width: `${item.progresso || 0}%`, 
                            height: '100%', 
                            backgroundColor: getProgressColor(item.progresso || 0),
                            borderRadius: '3px'
                          }}></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {obraFixaSelecionada.observacoes && (
                <div>
                  <strong>Observações:</strong>
                  <p style={{ 
                    marginTop: '8px', 
                    padding: '10px', 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}>
                    {obraFixaSelecionada.observacoes}
                  </p>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button className="button" onClick={() => setObraFixaSelecionada(null)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapaObrasFixas;